import { joinURL } from 'ufo'
import type { FetchResponse } from 'ofetch'

import type { AgentCookie } from '@layers/web/stores/types/root.ts'
import { API_DISALLOWED_FILETYPES } from '@layers/web/constants/api-fetch.ts'
import { BOOKING_PROCESS_MIN_TIMEOUT_MS, BOOKING_READ_NETWORK_RETRIES, BOOKING_READ_RETRY_DELAY_MS } from '@layers/web/constants/book.ts'
import localStorageKeys from '@layers/web/constants/local-storage-keys.ts'
import type { ApiOptionalOptions, RequestOptions } from '@layers/web/utils/types/api-fetch.ts'

const isValidFileType = (request: string, options: RequestOptions): boolean => {
  if (API_DISALLOWED_FILETYPES.some(filetype => request.endsWith(filetype))) {
    if (options.throwOnFail) {
      throw new Error('Not found: ' + request)
    }

    return false
  }

  return true
}

const isBookingProcessRequest = (request: string): boolean =>
  request.includes('/booking/') || request.includes('/charter/packages/offers/state')

const createRequestOptions = (options: ApiOptionalOptions, request: string): RequestOptions => {
  const defaultTimeout = isBookingProcessRequest(request) ? BOOKING_PROCESS_MIN_TIMEOUT_MS : 60 * 1000

  const requestOptions: RequestOptions = {
    throwOnFail: true,
    externalUrl: false,
    simple: false,
    timeout: options.timeout ?? defaultTimeout,
    raw: true,
    // The API answers JSON: parse it as JSON even when a proxy or in-app browser has doubled the
    // Content-Type header, which ofetch's own detection reads as a blob
    responseType: 'json',
    ...options,
  }

  const optionsWithBody = createRequestBody(requestOptions)
  return optionsWithBody
}

const createRequestBody = (options: RequestOptions): RequestOptions => {
  if (options.method?.toUpperCase() !== 'POST' || options.addToken !== true) {
    return options
  }

  const token = tryGetLocalStorage(localStorageKeys.auth.token)

  if (!token) {
    return options
  }

  /**
   * A string body is the payload itself, so there is nowhere to put the token
   */
  if (typeof options.body === 'string') {
    return options
  }

  options.body = { token, ...options.body }
  return options
}

const createRequestHeaders = (options: RequestOptions): Headers => {
  const rootStore = useRootStore()
  const headers = new Headers()

  if (import.meta.client) {
    headers.set('X-HREF', location.href)
  }

  if (options.simple) {
    return headers
  }

  const cookie = rootStore.rolfsCookie
  if (cookie) {
    headers.set('X-ROLFS-RF', cookie)
  }

  if (!rootStore.agentCookie) {
    return headers
  }

  const agentCookie = rootStore.agentCookie as AgentCookie

  if (agentCookie.code) {
    headers.set('X-ROLFS-A', agentCookie.code)
  }

  if (agentCookie.currentDate) {
    headers.set('X-ROLFS-ATIME', agentCookie.currentDate)
  }

  return headers
}

const logHttpRequest = (request: string, options: RequestOptions): void => {
  if (import.meta.env.PROD) {
    return
  }

  const configStore = useConfigStore()
  const { API_BASE_PATH } = configStore

  const requestMethod = options.method ?? 'GET'

  console.info(
    options.externalUrl
      ? `[apiFetch] Making external ${requestMethod} request to: ${request}`
      : `[apiFetch] Making ${requestMethod} request to: ${API_BASE_PATH}${request}`
  )
}

const logFailedHttpRequest = (error: any, throwOnFail: boolean = false): any => {
  if (process.env.NODE_ENV === 'development') {
    console.error('[apiFetch] Request failed: ', error)
  }

  if (!throwOnFail) {
    return error
  }

  throw error
}

const createApiUrl = (request: string, options: RequestOptions): string => {
  const { API_URL, API_BASE_PATH } = useConfigStore()
  const { externalUrl } = options

  if (externalUrl) {
    return request
  }

  const requestWithCacheReload = withBackendCacheReload(request)

  if (import.meta.client && !options.noProxy) {
    return `${API_BASE_PATH}${requestWithCacheReload}` // PROXY
  }

  return joinURL(API_URL, API_BASE_PATH) + requestWithCacheReload // API URI
}

const parseHttpResponseToCookie = (simple: boolean, response: Response): void => {
  const rootStore = useRootStore()
  if (simple || !response.headers.get('x-rolfs-rf') || rootStore.rolfsCookie) {
    return
  }

  const cookie = response.headers.get('x-rolfs-rf')

  rootStore.setRolfsCookie(cookie)
}

const makeHttpRequest = (
  uri: string,
  options: ApiOptionalOptions = {},
  headers?: Headers
): Promise<FetchResponse<Response>> => {
  return $fetch.raw(uri, {
    headers,
    ...options,
  }) as unknown as Promise<FetchResponse<Response>>
}

/**
 * Runs makeHttpRequest and retries only on errors that say nothing about the API itself.
 * ofetch's own retry is disabled here (retry: 0) because it cannot distinguish a
 * no-response network error from a real HTTP 500 — this loop is the single source
 * of truth. Retried: no-response network blips (IT-2112) and gateway 502/503/504 from
 * the Vercel hop in front of the API (IT-3432). Never retried: 4xx, 500 from the
 * API itself, aborts and timeouts. Reuses the caller's signal, so supersede-aborts and
 * the 120s timeout still abort and are (correctly) not retried.
 */
const requestWithNetworkRetry = async (
  uri: string,
  options: ApiOptionalOptions,
  headers?: Headers,
  retriesLeft: number = BOOKING_READ_NETWORK_RETRIES
): Promise<FetchResponse<Response>> => {
  try {
    return await makeHttpRequest(uri, { ...options, retry: 0 }, headers)
  } catch (error) {
    if (retriesLeft <= 0 || !(isTransientNetworkError(error) || isGatewayError(error))) {
      throw error
    }

    await waitMs(BOOKING_READ_RETRY_DELAY_MS)
    return requestWithNetworkRetry(uri, options, headers, retriesLeft - 1)
  }
}

/**
 * Abort existing abort controller if it exists
 */
const abortExistingRequest = (controllerKey: string): void => {
  if (!import.meta.client) {
    return
  }

  const windowWithControllers = window as unknown as Window & {
    [key: string]: AbortController | undefined;
  }

  const existingController = windowWithControllers[controllerKey]

  if (!existingController) {
    return
  }

  existingController.abort()
}

/**
 * Store abort controller on window for later abortion
 */
const storeAbortController = (abortController: AbortController, controllerKey: string): void => {
  if (!import.meta.client) {
    return
  }

  const windowWithControllers = window as unknown as Window & {
    [key: string]: AbortController | undefined;
  }

  windowWithControllers[controllerKey] = abortController
}

export {
  createApiUrl,
  createRequestBody,
  createRequestHeaders,
  createRequestOptions,
  isValidFileType,
  logFailedHttpRequest,
  logHttpRequest,
  parseHttpResponseToCookie,
  makeHttpRequest,
  requestWithNetworkRetry,
  abortExistingRequest,
  storeAbortController,
}
